import {
  BENEFIT_LABELS,
  serviceLineLabel,
} from "pages/HospitalsPage/components/BenefitSelection/constants";
import {
  BenefitServiceType,
  IPayerOverrideRow,
  PayerOverrideKind,
} from "types/BenefitSelection/BenefitSelection";

/**
 * CLE-2792 — the words the payer-overrides screen uses, in one place.
 *
 * 🚨 "REPLACING", "CORRECTING", "MODE" AND "AFTER SOURCE" ARE NEVER SHOWN TO A USER. The API's three
 * kinds each have exactly one sentence, and the sentence carries the amount and the benefit so it
 * can be read on its own — an editor deciding between three shapes should be reading three
 * sentences about payers, not two synonyms out of the engine's own vocabulary.
 *
 * 🚨 "SERVICE LINE", NEVER "BLOCK" OR "CARE SETTING". "LINE TYPE", NEVER "ROLE". Same words as the
 * account card and the line-types tab, because a reader checking an override against an account's
 * configuration is comparing two screens.
 */

/** A percent for co-insurance, dollars for every other benefit. */
export const formatOverrideValue = (value: number, benefit: string): string => {
  if (benefit === "COINSURANCE_PERCENT") {
    return `${Number.isInteger(value) ? value : value.toFixed(2)}%`;
  }
  return `$${value.toLocaleString("en-US", {
    minimumFractionDigits: Number.isInteger(value) ? 0 : 2,
    maximumFractionDigits: 2,
  })}`;
};

export const benefitLabel = (benefit: string): string =>
  BENEFIT_LABELS[benefit] ?? benefit;

/** The benefit in the middle of a sentence, where a capitalised label reads as a heading. */
const benefitInSentence = (benefit: string): string =>
  benefitLabel(benefit).toLowerCase();

/**
 * The three kinds, as the radio buttons put them — no amount, no benefit, because the form has both
 * of those in their own fields and the reader is choosing a shape.
 */
export const KIND_CHOICES: Array<{
  value: PayerOverrideKind;
  label: string;
  help: string;
}> = [
  {
    value: "ALWAYS",
    label: "The payer never reports this — always use the amount below",
    help: "The amount is used whatever the response says, and the usual line selection does not run.",
  },
  {
    value: "WHEN_MISSING",
    label:
      "Use the amount below when a service line has no value for this benefit",
    help: "The response is read first. The amount is used only where that service line answered with nothing.",
  },
  {
    value: "SWAP",
    label:
      "The payer reports the wrong amount — swap what it reports for the amount below",
    help: "Applied only where the response already reported an amount, and only if that amount is different.",
  },
];

/**
 * One row as a full sentence.
 *
 * WHEN_MISSING names the service line and the benefit, because "use $200 when it is missing" is
 * ambiguous about missing WHERE — and that ambiguity is the difference between a flat figure and a
 * figure that defers to the payer on 514 checks out of 688.
 */
export const overrideSentence = (row: IPayerOverrideRow): string => {
  const amount = formatOverrideValue(row.value, row.benefit);

  if (row.kind === "ALWAYS") {
    return `The payer never reports this — always use ${amount}`;
  }
  if (row.kind === "WHEN_MISSING") {
    return `Use ${amount} when ${serviceLineLabel(
      row.afterSource as BenefitServiceType | null
    )} has no ${benefitInSentence(row.benefit)}`;
  }
  return `The payer reports the wrong amount — swap what it reports for ${amount}${
    row.correctsZero ? ", including a reported $0" : ""
  }`;
};

/**
 * Every scope field the row states, each as "<field>: <value>", in the order the engine checks them.
 *
 * 🚨 AN EMPTY LIST IS NOT "NO SCOPE SHOWN", IT IS "EVERY ESTIMATE". A row that names nothing governs
 * every payer, every plan and every account, and a blank cell would read as missing data rather than
 * as the widest possible rule — so the caller renders the sentence, never the empty list.
 */
export const scopeParts = (row: IPayerOverrideRow): string[] => {
  const parts: string[] = [];

  if (row.payerId !== null) {
    parts.push(`Payer: ${row.payerName ?? `#${row.payerId}`}`);
  }
  if (row.requestedPayerName !== null) {
    parts.push(`Payer name as typed: ${row.requestedPayerName}`);
  }
  if (row.planName !== null) parts.push(`Plan: ${row.planName}`);
  if (row.policyType !== null) parts.push(`Policy type: ${row.policyType}`);
  if (row.readFromSource !== null) {
    // CLE-2792 — a condition about the READING, not about the account. Printed in the same voice as
    // the sentence above it: "read from Ambulatory surgery center".
    parts.push(
      `read from ${serviceLineLabel(
        row.readFromSource as BenefitServiceType | null
      )}`
    );
  }
  if (row.accountId !== null) {
    parts.push(`Account: ${row.accountName ?? row.accountId}`);
  }
  if (row.practiceTypeCode !== null) {
    parts.push(`Service type code: ${row.practiceTypeCode}`);
  }

  return parts;
};

export const EVERY_ESTIMATE = "Every payer and every account";

export const scopeSentence = (row: IPayerOverrideRow): string => {
  const parts = scopeParts(row);
  return parts.length === 0 ? EVERY_ESTIMATE : parts.join(" · ");
};

/**
 * 🚨 RENDERED IN UTC, DELIBERATELY. A plan-year boundary is stored as midnight UTC, and a viewer
 * west of it sees the calendar day BEFORE the one the engine compares the date of service against —
 * a row effective 2026-01-01 reading as 12/31/2025 on every screen in the United States. The date is
 * a plan-year fact, not a moment, so it is printed as the day it names.
 */
const asDate = (value: string | null): string | null =>
  value === null
    ? null
    : new Date(value).toLocaleDateString("en-US", { timeZone: "UTC" });

/** Null on a row with no window at all, which is every seeded row. */
export const datesSentence = (row: IPayerOverrideRow): string | null => {
  const from = asDate(row.effectiveFrom);
  const to = asDate(row.effectiveTo);

  if (from && to) return `${from} – ${to}`;
  if (from) return `From ${from}`;
  if (to) return `Until ${to}`;
  return null;
};
